import type {
  CompanyData,
  PersonData,
  UserResponse,
} from "@onestore/api/account"
import type {
  Basket,
  BasketParamListItem,
  BasketParamsResponse,
} from "@onestore/api/basket"
import type { JSONPatch } from "@onestore/api/types"
import type { HTTP } from "@onestore/onestore-store-common"
import {
  HTTP_STATUS,
  isResponsePending,
} from "@onestore/onestore-store-common/http"
import { BasketParamsCollection } from "@onestore/onestore-store-common/params"
import StoreAPI from "~/lib/api"
import { forceRedirectToIndex } from "~/lib/browser"
import { getCountryCode } from "~/lib/config"
import { STORAGE } from "~/lib/storage"
import LocalStorage from "~/lib/storage/LocalStorage"
import { termsSuccessActionCreator } from "~/store/checkout-terms/actions"
import { CheckoutTermsActionType } from "~/store/checkout-terms/constants"
import { type BasketSuccessAction, getBasket } from "../basket/actions"
import { BasketActionSource, BasketActions } from "../basket/constants"
import type { AppDispatch, AppState } from "../reducer"
import { CheckoutActions } from "./constants"

export type ResponseStatusAction<Type> = {
  type: Type
  response_status_code: HTTP.Status
}

type ParamsGetAction<Type> = ResponseStatusAction<Type> & {
  params: Record<string, BasketParamListItem>
}

type AlternativeResponseStatusAction<Type> = {
  type: Type
  status: HTTP.Status
}

type AuthSuccessAction<Type> = AlternativeResponseStatusAction<Type> & {
  login: string
  accountExists: boolean
  hitId?: string
  accountId?: number
  signature: string
}

type ExternalAuthSuccessAction<Type> = AlternativeResponseStatusAction<Type> & {
  login: string
  accountExists: boolean
  hitId?: string
  accountId?: number
  signature: string
  redirectPath: string
  newToken: string
}

export type SimpleAction<Type> = {
  type: Type
}

export type ExternalAuthDoneAction = {
  type: CheckoutActions.EXTERNAL_AUTH_DONE
  redirectPath: string
}

export type AddressConfirmationOpenAction = {
  type: CheckoutActions.ADDRESS_CONFIRMATION_OPEN
  user: CompanyData | PersonData
}

type AuthLoginSaveAction<Type> = SimpleAction<Type> & {
  login: string
  accountExists: boolean
  checkUserRequest: HTTP.Status
  authUserRequest: HTTP.Status
}

type UserRegisterSuccessAction = {
  type: CheckoutActions.USER_REGISTER_SUCCESS
  result: CompanyData | PersonData
  response_status_code: HTTP.Status
  hitId?: string
  accountId?: number
  signature: string
}

type UserUpdateSuccessAction = {
  type: CheckoutActions.USER_UPDATE_SUCCESS
  result: CompanyData | PersonData
  response_status_code: HTTP.Status
}

type UserSuccessAction = {
  type: CheckoutActions.USER_SUCCESS
  response_status_code: HTTP.Status
  result: UserResponse
  is_after_login: boolean
}

export type CheckoutAction =
  | UserSuccessAction
  | AuthLoginSaveAction<CheckoutActions.AUTH_LOGIN_RESET>
  | AuthLoginSaveAction<CheckoutActions.AUTH_LOGIN_SKIP>
  | AlternativeResponseStatusAction<CheckoutActions.AUTH_PENDING>
  | AlternativeResponseStatusAction<CheckoutActions.AUTH_FAILURE>
  | ParamsGetAction<CheckoutActions.BASKET_PARAMS_GET_SUCCESS>
  | AuthSuccessAction<CheckoutActions.AUTH_SUCCESS>
  | ExternalAuthSuccessAction<CheckoutActions.EXTERNAL_AUTH_SUCCESS>
  | SimpleAction<CheckoutActions.CHECKOUT_RESET>
  | SimpleAction<CheckoutActions.RESET>
  | SimpleAction<CheckoutActions.BASKET_SUCCESS>
  | SimpleAction<CheckoutActions.USER_PENDING>
  | SimpleAction<CheckoutActions.BASKET_PENDING>
  | SimpleAction<CheckoutActions.BASKET_PARAMS_GET_PENDING>
  | SimpleAction<CheckoutActions.BASKET_PARAMS_SAVE_PENDING>
  | SimpleAction<CheckoutActions.BASKET_FAILURE>
  | SimpleAction<CheckoutActions.ADDRESS_CONFIRMATION_CLOSE>
  | SimpleAction<CheckoutActions.USER_EDIT_DATA_MODAL_CLOSE>
  | SimpleAction<CheckoutActions.USER_EDIT_DATA_MODAL_OPEN>
  | ExternalAuthDoneAction
  | AddressConfirmationOpenAction
  | ResponseStatusAction<CheckoutActions.BASKET_PARAMS_SAVE_SUCCESS>
  | ResponseStatusAction<CheckoutActions.BASKET_PARAMS_SAVE_FAILURE>
  | UserRegisterSuccessAction
  | UserUpdateSuccessAction
  | ResponseStatusAction<CheckoutActions.USER_FAILURE>
  | ResponseStatusAction<CheckoutActions.BASKET_PARAMS_GET_FAILURE>

export const getBasketForSummary = () => {
  return (dispatch: AppDispatch, getState: { (): AppState }) => {
    const state = getState()

    if (isResponsePending(state.checkoutTerms.termsRequest)) {
      return
    }

    dispatch({
      type: CheckoutTermsActionType.TERMS_PENDING,
    })

    const token = state.basket.token

    if (!token) {
      forceRedirectToIndex()
    }

    return StoreAPI.basketGet(token, BasketActionSource.SUMMARY)
      .catch(() => {
        dispatch({
          type: CheckoutTermsActionType.TERMS_FAILURE,
          response_status_code: HTTP_STATUS.INTERNAL_SERVER_ERROR,
        })
      })
      .then((result) => {
        if (undefined === result) {
          return
        }

        dispatch({
          type: BasketActions.BASKET_SUCCESS,
          result,
        } as BasketSuccessAction)

        dispatch(termsSuccessActionCreator(result))
      })
  }
}

// Refetches the basket in the background (e.g. on tab focus) so payment
// methods removed elsewhere don't linger in the Summary view. Only
// BASKET_SUCCESS is dispatched: the PENDING actions used by
// getBasketForSummary flip the loading selectors read by the payment/total
// aside and make it flash on every refetch. Errors are ignored — the current
// basket stays in place and the next focus retries.
export const refreshBasketSilently = () => {
  return async (dispatch: AppDispatch, getState: { (): AppState }) => {
    const state = getState()

    if (isResponsePending(state.basket.request_state)) {
      return
    }

    const token = state.basket.token

    if (!token) {
      return
    }

    const result = await StoreAPI.basketGet(
      token,
      BasketActionSource.SUMMARY
    ).catch(() => undefined)

    if (undefined === result) {
      return
    }

    dispatch({
      type: BasketActions.BASKET_SUCCESS,
      result,
    } as BasketSuccessAction)
  }
}

export const getBasketParams = () => {
  return (dispatch: AppDispatch, getState: { (): AppState }) => {
    const state = getState()

    if (isResponsePending(state.basket.get_params_request)) {
      return
    }
    dispatch({
      type: CheckoutActions.BASKET_PARAMS_GET_PENDING,
    })

    const token = state.basket.token

    if (!token) {
      forceRedirectToIndex()
    }

    return StoreAPI.getBasketParams(token)
      .catch(() => {
        dispatch({
          type: CheckoutActions.BASKET_PARAMS_GET_FAILURE,
          response_status_code: HTTP_STATUS.INTERNAL_SERVER_ERROR,
        })
      })
      .then((result: BasketParamsResponse) => {
        dispatch({
          type: CheckoutActions.BASKET_PARAMS_GET_SUCCESS,
          response_status_code: HTTP_STATUS.OK,
          params: BasketParamsCollection(result),
        })
      })
  }
}

export function reset() {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.RESET,
    })
  }
}

export function handleSuccessfullLogin(
  dispatch: AppDispatch,
  login: string,
  token: Basket.Token
) {
  dispatch(
    savePersonData({
      person_details: {
        country: getCountryCode(),
      },
      credentials: {
        email: login,
      },
    })
  )
  dispatch(
    saveCompanyData({
      company_details: {
        country: getCountryCode(),
      },
      credentials: {
        email: login,
      },
    })
  )

  dispatch(getUser(token, true))
}

export function resetUserLogin() {
  return (dispatch: AppDispatch) => {
    LocalStorage.remove(STORAGE.USER_LOGIN)
    LocalStorage.remove(STORAGE.LOGIN_EXISTS)

    dispatch({
      type: CheckoutActions.AUTH_LOGIN_RESET,
      login: null,
      accountExists: false,
      isAuthenticated: false,
      status: HTTP_STATUS.NONE,
    })
  }
}

export function skipLoginForm() {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.AUTH_LOGIN_SKIP,
      login: "",
      accountExists: false,
      isAuthenticated: false,
    })
  }
}

export function getUser(token?: Basket.Token, isAfterLogin?: boolean) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.USER_PENDING,
    })

    if (!token) {
      forceRedirectToIndex()

      return
    }

    return StoreAPI.getUser(token)
      .catch(() => {
        dispatch({
          type: CheckoutActions.USER_FAILURE,
          response_status_code: HTTP_STATUS.BAD_REQUEST,
        })
      })
      .then((result) => {
        if (undefined === result) {
          return
        }
        dispatch({
          type: CheckoutActions.USER_SUCCESS,
          response_status_code: HTTP_STATUS.OK,
          result,
          is_after_login: !!isAfterLogin,
        })
      })
  }
}

export function saveBasketParams(operations: JSONPatch[]) {
  return (dispatch: AppDispatch, getState: { (): AppState }) => {
    dispatch({
      type: CheckoutActions.BASKET_PARAMS_SAVE_PENDING,
    })

    const token = getState().basket.token

    if (!token) {
      forceRedirectToIndex()

      return
    }

    return StoreAPI.basketPatch(token, operations)
      .catch(() => {
        dispatch({
          type: CheckoutActions.BASKET_PARAMS_SAVE_FAILURE,
          response_status_code: HTTP_STATUS.INTERNAL_SERVER_ERROR,
        })
      })
      .then((response) => {
        if (undefined === response) {
          return
        }

        dispatch({
          type: CheckoutActions.BASKET_PARAMS_SAVE_SUCCESS,
          response_status_code: HTTP_STATUS.OK,
        })

        dispatch(getBasket(token, false))
      })
  }
}

export function resetCheckoutRequestState() {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.CHECKOUT_RESET,
    })
  }
}

export function userDataEditModalOpen() {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.USER_EDIT_DATA_MODAL_OPEN,
    })
  }
}

export function userDataEditModalClose() {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.USER_EDIT_DATA_MODAL_CLOSE,
    })
  }
}

export function saveCompanyData(data) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.COMPANY_DATA_SAVE,
      data,
    })
  }
}

export function savePersonData(data) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.PERSON_DATA_SAVE,
      data,
    })
  }
}

export function saveMigratedCompanyData(data) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.COMPANY_DATA_SAVE,
      data,
    })
  }
}

export function saveMigratedPersonData(data) {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: CheckoutActions.PERSON_DATA_SAVE,
      data,
    })
  }
}
